import React, { useMemo } from 'react';
import Select, { GroupBase, SingleValue } from 'react-select';
import classNames from 'classnames';

import { MdAdd } from 'react-icons/md';
import { FiXCircle } from 'react-icons/fi';

import ScoreTab from 'components/common/scoreTab';

import { en } from 'constants/lang/en';

import type { SkillGroupOptions } from '../types';

type GroupOption = SkillGroupOptions[number];
type LeafOption = GroupOption['options'][number];

export interface SkillsSelectorProps {
  skillOptions: SkillGroupOptions;
  loading: boolean;
  onAddSkill: () => void;
  onRemoveSkill: (index: number) => void;
  skills: Array<number | string | null>;
  ratings: Array<number | string | null | undefined>;
  rowIds: string[];
  onSkillChange: (index: number, value: number | string | undefined) => void;
  onRatingChange: (index: number, value: number | null) => void;
}

const SkillsSelector: React.FC<SkillsSelectorProps> = ({
  skillOptions,
  loading,
  onAddSkill,
  onRemoveSkill,
  skills,
  ratings,
  rowIds,
  onSkillChange,
  onRatingChange,
}) => {
  const filterSkillOptions = useMemo((): GroupBase<LeafOption>[] => {
    const skillsMap: Record<string, boolean> = {};

    skills.forEach((element) => {
      if (element !== null && element !== undefined) {
        skillsMap[String(element)] = true;
      }
    });

    return skillOptions.map((skillOption) => ({
      label: skillOption.label,
      options: skillOption.options.filter((option) => !skillsMap[String(option.value)]),
    }));
  }, [skillOptions, skills]);

  return (
    <>
      {skills.map((skill, index) => {
        const rowId = rowIds[index] || `skill-row-${index}`;
        return (
          <div className="skill-select-node d-flex align-items-end column-gap-3x" key={rowId}>
            <div className="skill-select">
              <div className="skill-label filter-label font-12">
                Skill {index + 1} {index === 0 && <span className="c-red">*</span>}
              </div>

              <Select<LeafOption, false, GroupBase<LeafOption>>
                options={filterSkillOptions}
                isLoading={loading}
                isSearchable={true}
                isClearable={true}
                value={
                  skillOptions.flatMap((item) => item.options).find((option) => option.value === +String(skill)) ?? null
                }
                onChange={(option: SingleValue<LeafOption>) => {
                  const value = option?.value;
                  onSkillChange(index, value);
                  if (ratings[index] == null && value !== undefined && value !== null) {
                    onRatingChange(index, 0);
                  }
                }}
              />
            </div>

            <div className="rating-select">
              <div className="rating-label filter-label font-12">{'Ratings'}</div>

              <ScoreTab
                lightMode={true}
                scoreId={`resource-finder-skill-${rowId}`}
                name={`resource-finder-skill-${rowId}`}
                scoreValue={ratings[index] === null || ratings[index] === undefined ? null : +ratings[index]}
                handleChange={(_name: string, value: number | null) => {
                  onRatingChange(index, value);
                }}
              />
            </div>

            <div
              className={classNames('h-10x d-flex align-items-center invisible', {
                visible: skills.length > 1,
              })}
            >
              <button className="clear-btn" type="button" onClick={() => onRemoveSkill(index)}>
                <FiXCircle size={20} />
              </button>
            </div>
          </div>
        );
      })}

      <div className="add-more-skill-filter" onClick={onAddSkill}>
        <MdAdd size={20} />

        <span className="skill-add-label">{en.BUTTON.ADD_ANOTHER_SKILL_FILTER}</span>
      </div>
    </>
  );
};

export default SkillsSelector;
